<template>
  <div v-if="showControls" class="pagination-controls">
    <template v-if="isServerMode">
      <div class="pagination-summary">
        <span class="results-copy">
          Exibindo {{ serverItemsLabel }} de {{ limit }}
          (pagina {{ serverCurrentPage }})
        </span>
      </div>

      <div class="pagination-actions">
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="isPrevDisabled"
          @click="$emit('prev')"
        >
          Anterior
        </button>

        <button
          type="button"
          class="btn btn-secondary"
          :disabled="isNextDisabled"
          @click="$emit('next')"
        >
          Proxima
        </button>
      </div>
    </template>

    <template v-else>
      <div class="pagination-summary">
        <label class="page-size-control">
          <span>Itens por pagina</span>
          <select :value="itemsPerPage" :disabled="disabled" @change="handleItemsPerPageChange">
            <option v-for="option in pageSizeOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
        </label>

        <span class="results-copy">
          Exibindo {{ firstItem }}-{{ lastItem }} de {{ totalItems }}
        </span>
      </div>

      <div class="pagination-actions">
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="disabled || currentPage <= 1"
          @click="emitPageChange(currentPage - 1)"
        >
          Anterior
        </button>

        <span class="pagination-copy">Pagina {{ currentPage }} de {{ normalizedTotalPages }}</span>

        <label v-if="showJumpToPage" class="jump-to-page">
          <span>Ir para</span>
          <input
            type="number"
            min="1"
            :max="normalizedTotalPages"
            :value="jumpPage"
            :disabled="disabled"
            @input="handleJumpInput"
            @keyup.enter="goToJumpPage"
          >
          <button type="button" class="btn btn-secondary" :disabled="disabled" @click="goToJumpPage">
            Ir
          </button>
        </label>

        <button
          type="button"
          class="btn btn-secondary"
          :disabled="disabled || currentPage >= normalizedTotalPages"
          @click="emitPageChange(currentPage + 1)"
        >
          Proxima
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'

export default defineComponent({
  name: 'PaginationControls',
  props: {
    currentPage: {
      type: Number,
      default: 1
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    totalItems: {
      type: Number,
      default: 0
    },
    totalPages: {
      type: Number,
      default: 1
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showJumpToPage: {
      type: Boolean,
      default: true
    },
    pageSizeOptions: {
      type: Array as () => number[],
      default: () => [10, 25, 50, 100]
    },
    limit: {
      type: Number,
      default: null
    },
    offset: {
      type: Number,
      default: null
    },
    pageItemsCount: {
      type: Number,
      default: null
    }
  },
  emits: ['update:currentPage', 'update:itemsPerPage', 'prev', 'next'],
  setup(props, { emit }) {
    const jumpPage = ref(String(props.currentPage))

    const isServerMode = computed(() =>
      typeof props.limit === 'number' &&
      typeof props.offset === 'number' &&
      typeof props.pageItemsCount === 'number'
    )

    const normalizedTotalPages = computed(() => Math.max(props.totalPages, 1))
    const serverCurrentPage = computed(() => {
      if (!isServerMode.value || !props.limit) {
        return 1
      }

      return Math.floor((props.offset || 0) / props.limit) + 1
    })
    const serverItemsLabel = computed(() => props.pageItemsCount ?? 0)
    const isPrevDisabled = computed(() => props.disabled || (props.offset ?? 0) === 0)
    const isNextDisabled = computed(() =>
      props.disabled || (props.pageItemsCount ?? 0) < (props.limit ?? 0)
    )

    const showControls = computed(() => {
      if (isServerMode.value) {
        return (props.pageItemsCount ?? 0) > 0 || (props.offset ?? 0) > 0
      }

      return props.totalItems > 0
    })

    const firstItem = computed(() => {
      if (props.totalItems === 0) {
        return 0
      }

      return (props.currentPage - 1) * props.itemsPerPage + 1
    })

    const lastItem = computed(() => Math.min(props.currentPage * props.itemsPerPage, props.totalItems))

    watch(
      () => props.currentPage,
      (page) => {
        jumpPage.value = String(page)
      },
      { immediate: true }
    )

    const emitPageChange = (page: number) => {
      const normalizedPage = Math.min(Math.max(page, 1), normalizedTotalPages.value)
      emit('update:currentPage', normalizedPage)
    }

    const handleItemsPerPageChange = (event: Event) => {
      const target = event.target as HTMLSelectElement
      const nextValue = Number(target.value)

      if (!Number.isNaN(nextValue)) {
        emit('update:itemsPerPage', nextValue)
      }
    }

    const handleJumpInput = (event: Event) => {
      const target = event.target as HTMLInputElement
      jumpPage.value = target.value
    }

    const goToJumpPage = () => {
      const parsedPage = Number(jumpPage.value)

      if (Number.isNaN(parsedPage)) {
        jumpPage.value = String(props.currentPage)
        return
      }

      emitPageChange(parsedPage)
    }

    return {
      showControls,
      isServerMode,
      jumpPage,
      firstItem,
      lastItem,
      normalizedTotalPages,
      serverCurrentPage,
      serverItemsLabel,
      isPrevDisabled,
      isNextDisabled,
      emitPageChange,
      handleItemsPerPageChange,
      handleJumpInput,
      goToJumpPage
    }
  }
})
</script>

<style scoped>
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 12px 14px;
}

.pagination-summary,
.pagination-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.page-size-control,
.jump-to-page {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 14px;
}

.page-size-control select,
.jump-to-page input {
  min-height: 38px;
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
}

.jump-to-page input {
  width: 72px;
}

.pagination-copy,
.results-copy {
  color: var(--text-secondary);
  font-size: 14px;
}

@media (max-width: 720px) {
  .pagination-controls {
    align-items: stretch;
  }

  .pagination-summary,
  .pagination-actions {
    width: 100%;
  }

  .pagination-actions {
    justify-content: space-between;
  }
}
</style>
